import * as React from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type TruncatedTextProps = {
  /** Full, untruncated text — shown in the tooltip / on tap-reveal. */
  text: string;
  /** Element to render for the (clipped) label. Defaults to a `span`. */
  as?: "span" | "div" | "p";
  /** Class applied to the trigger element. `truncate` is always included. */
  className?: string;
  /**
   * Optional custom rendered content for the label (e.g. a search highlight or
   * an inline icon). Falls back to `text` when omitted. The tooltip always
   * shows the plain `text`.
   */
  children?: React.ReactNode;
  side?: React.ComponentProps<typeof TooltipContent>["side"];
  align?: React.ComponentProps<typeof TooltipContent>["align"];
  contentClassName?: string;
  "data-testid"?: string;
};

/**
 * A truncated label that reveals its full text on hover (desktop) AND on tap
 * (touch devices). Native `title` tooltips only fire on mouse hover, leaving
 * clipped labels permanently unreadable on phones/tablets — this closes that
 * gap with a Radix tooltip.
 *
 * Behaviour:
 * - Desktop: Radix opens the tooltip on hover/focus, but only when the label is
 *   actually clipped (so short labels don't sprout redundant tooltips).
 * - Touch: Radix suppresses open-on-tap, so we toggle it ourselves. The tap is
 *   only intercepted (preventing an enclosing row/link from navigating) when the
 *   label is truly clipped; otherwise the tap falls through untouched.
 */
export function TruncatedText({
  text,
  as = "span",
  className,
  children,
  side = "top",
  align = "start",
  contentClassName,
  "data-testid": testId,
}: TruncatedTextProps) {
  const Comp = as as React.ElementType;
  const ref = React.useRef<HTMLElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const pointerType = React.useRef<string>("mouse");

  const isOverflowing = React.useCallback(() => {
    const el = ref.current;
    return !!el && el.scrollWidth - el.clientWidth > 1;
  }, []);

  // Desktop hover / keyboard focus: let Radix drive the open state, but only
  // when the label is actually clipped.
  const handleOpenChange = (next: boolean) => {
    if (next && !isOverflowing()) return;
    setOpen(next);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    pointerType.current = e.pointerType;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (pointerType.current !== "touch") return;
    if (!isOverflowing()) return;
    // Tap-to-reveal: don't let an enclosing button/link act on this tap.
    e.preventDefault();
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  // Once revealed by tap, dismiss on the next tap elsewhere.
  React.useEffect(() => {
    if (!open) return;
    const onDocPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, [open]);

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={open} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <Comp
            ref={ref}
            className={cn("truncate", className)}
            onPointerDown={handlePointerDown}
            onClick={handleClick}
            data-testid={testId}
          >
            {children ?? text}
          </Comp>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className={cn(
            "max-w-[min(90vw,20rem)] whitespace-normal break-words",
            contentClassName,
          )}
        >
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
